/**
 * Register — Eldritch Eye edition.
 *
 * Required: email + password.
 * Optional (FULL bonus credits if provided): full_name, phone, address, GPS.
 *
 * Telegram Login Widget as alternative entry. Anti-multi-cuenta is enforced
 * server-side via device fingerprint + IP cap.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useWebAuth } from "@/lib/webAuth";
import { getDeviceFingerprint } from "@/lib/deviceFingerprint";
import useGeolocation from "@/hooks/useGeolocation";
import webApi from "@/lib/webApi";
import CodeRain from "@/components/effects/CodeRain";
import {
  Loader2, Mail, Lock, User, Phone, AlertTriangle,
  MapPin, Gift, ChevronDown, ChevronUp, Eye,
} from "lucide-react";

function formatError(err) {
  // Accept either a raw error object or a `detail` value.
  if (err && typeof err === "object" && (err.response || err.request || err.message)) {
    const detail = err.response?.data?.detail;
    if (detail) {
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail))
        return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
      return String(detail);
    }
    if (err.response?.status) {
      return `Error del servidor (HTTP ${err.response.status}). Intenta de nuevo en unos segundos.`;
    }
    if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      return "Sin conexión con el servidor. Revisa tu internet o intenta de nuevo en unos segundos.";
    }
    if (err.code === "ECONNABORTED") {
      return "Tiempo de espera agotado. Intenta de nuevo.";
    }
    return err.message || "Algo salió mal. Intenta de nuevo.";
  }
  // Legacy: caller passed a string detail directly.
  const detail = err;
  if (!detail) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function WebRegister() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { register, user, loading } = useWebAuth();
  const geo = useGeolocation();
  const [fp, setFp] = useState("");
  const [deviceTaken, setDeviceTaken] = useState(false);
  const [publicInfo, setPublicInfo] = useState({
    welcome_credits: 100,
    welcome_credits_partial: 25,
    referral_invitee_bonus_credits: 20,
  });
  const initialRef = (
    params.get("ref") || params.get("referral") || ""
  ).trim().toUpperCase();
  const [referralInfo, setReferralInfo] = useState(null);
  const [showOptional, setShowOptional] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "+52",
    address: "",
    password: "",
    confirm: "",
    referral_code: initialRef,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) nav("/chat", { replace: true });
  }, [loading, user, nav]);

  useEffect(() => {
    (async () => {
      const f = await getDeviceFingerprint();
      setFp(f);
      try {
        const r = await webApi.get(`/device-check?fp=${f}`);
        setDeviceTaken(!!r.data.registered);
      } catch (e) {
        console.error("[register] device-check failed:", e);
      }
    })();
    webApi
      .get("/public/info")
      .then((r) => {
        setPublicInfo({
          welcome_credits: r.data.welcome_credits ?? 100,
          welcome_credits_partial: r.data.welcome_credits_partial ?? 25,
          referral_invitee_bonus_credits:
            r.data.referral_invitee_bonus_credits ?? 20,
        });
      })
      .catch(() => null);
  }, []);

  useEffect(() => {
    const c = (form.referral_code || "").trim().toUpperCase();
    let cancelled = false;
    if (!c) {
      const id = setTimeout(() => {
        if (!cancelled) setReferralInfo(null);
      }, 0);
      return () => {
        cancelled = true;
        clearTimeout(id);
      };
    }
    const t = setTimeout(() => {
      webApi
        .get(`/public/referral/${encodeURIComponent(c)}`)
        .then((r) => {
          if (!cancelled) setReferralInfo(r.data);
        })
        .catch(() => {
          if (!cancelled) setReferralInfo({ valid: false });
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.referral_code]);

  const profileComplete = !!(form.full_name.trim() && form.phone.trim().length > 3);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    if (form.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (!fp) {
      setError("No se pudo identificar el dispositivo. Recarga la página.");
      return;
    }
    let lat = geo.coords?.latitude;
    let lon = geo.coords?.longitude;
    if (lat == null && geo.status === "idle") {
      const c = await geo.request();
      lat = c?.latitude;
      lon = c?.longitude;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        device_fingerprint: fp,
      };
      if (form.full_name.trim()) payload.full_name = form.full_name.trim();
      if (form.phone.trim() && form.phone.trim() !== "+52")
        payload.phone = form.phone.trim();
      if (form.address.trim()) payload.address = form.address.trim();
      if (lat != null) payload.latitude = lat;
      if (lon != null) payload.longitude = lon;
      if (form.referral_code.trim())
        payload.referral_code = form.referral_code.trim().toUpperCase();
      await register(payload);
      nav("/chat", { replace: true });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const fullBonus = publicInfo.welcome_credits;
  const partialBonus = publicInfo.welcome_credits_partial;
  const refBonus = publicInfo.referral_invitee_bonus_credits;

  return (
    <div
      className="relative min-h-[100dvh] flex items-center justify-center bg-obsidian text-white overflow-hidden p-4 py-10 font-sans"
      data-testid="register-page"
    >
      <div className="fixed inset-0 z-0 bg-obsidian">
        <CodeRain density={0.4} speed={0.5} fade={0.08} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 30% 20%, rgba(255,26,58,0.18) 0%, transparent 50%), radial-gradient(ellipse at 70% 90%, rgba(0,224,255,0.10) 0%, transparent 50%)",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/"
          className="block text-center mb-4 font-mono text-[10px] tracking-[0.4em] text-zinc-500 hover:text-rune-red transition-colors"
          data-testid="back-home"
        >
          ← VOLVER AL OJO
        </Link>

        <div
          className="glass-panel-blood clip-rune relative overflow-hidden"
          data-testid="register-card"
        >
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, #ff1a3a 30%, #00e0ff 70%, transparent)",
              boxShadow: "0 0 10px rgba(255,26,58,0.8)",
            }}
          />

          <div className="p-7">
            <div className="text-center mb-5">
              <div
                className="inline-flex items-center justify-center w-12 h-12 mb-3 rounded-sm border border-rune-red/50"
                style={{
                  background:
                    "radial-gradient(ellipse at 30% 30%, #ff1a3a 0%, #b8001f 40%, #220006 100%)",
                  boxShadow:
                    "0 0 24px rgba(255, 26, 58, 0.5), inset 0 0 12px rgba(0,0,0,0.5)",
                }}
              >
                <Eye size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <h1
                className="font-runic text-2xl tracking-[0.18em] text-white"
                style={{
                  textShadow:
                    "0 0 18px rgba(255, 26, 58, 0.45), 0 0 4px rgba(255,255,255,0.4)",
                }}
                data-testid="register-title"
              >
                INVOCAR ACCESO
              </h1>
              <div className="text-[9px] font-mono tracking-[0.5em] text-data-cyan mt-1.5">
                ── INICIA TU SESIÓN ──
              </div>
            </div>

            {/* Bonus tip */}
            <div
              className="mb-4 p-3 border border-rune-red/40 rounded-sm flex items-start gap-2"
              style={{ background: "rgba(40, 0, 10, 0.4)" }}
              data-testid="bonus-hint"
            >
              <Gift size={14} className="text-rune-red mt-0.5 flex-shrink-0" />
              <div className="text-[11px] leading-relaxed text-zinc-200 font-mono">
                <span className="text-rune-red font-bold">+{fullBonus} créditos</span>{" "}
                de bienvenida (chat / imagen / voz / búsqueda).
              </div>
            </div>

            {referralInfo?.valid && (
              <div
                className="mb-4 p-3 border border-data-cyan/40 rounded-sm flex items-start gap-2"
                style={{ background: "rgba(0, 30, 40, 0.5)" }}
                data-testid="referral-info"
              >
                <Gift size={14} className="text-data-cyan mt-0.5 flex-shrink-0" />
                <div className="text-[11px] leading-relaxed text-zinc-200 font-mono">
                  Te invitó{" "}
                  <span className="text-data-cyan font-bold">
                    @{referralInfo.username || referralInfo.first_name || "amigo"}
                  </span>
                  . Recibirás{" "}
                  <span className="text-data-cyan font-bold">
                    +{refBonus} créditos sin restricciones
                  </span>
                  .
                </div>
              </div>
            )}

            {deviceTaken && (
              <div
                className="flex items-start gap-2 p-3 mb-5 border border-rune-red/40 text-rune-red text-xs font-mono rounded-sm"
                style={{ background: "rgba(40, 0, 10, 0.4)" }}
                data-testid="device-warning"
              >
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <div>
                  Este dispositivo ya tiene una cuenta. No podrás crear otra
                  desde aquí.
                  <br />
                  <Link
                    to="/login"
                    className="text-data-cyan underline mt-1 inline-block hover:text-white"
                  >
                    Inicia sesión &rarr;
                  </Link>
                </div>
              </div>
            )}

            <form
              onSubmit={submit}
              className="space-y-3"
              data-testid="register-form"
              autoComplete="on"
            >
              <Field
                icon={Mail}
                type="email"
                placeholder="email@dominio.com"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testid="register-email"
                required
                autoComplete="username email"
              />
              <Field
                icon={Lock}
                type="password"
                placeholder="contraseña (mín 8 caracteres)"
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })}
                testid="register-password"
                required
                autoComplete="new-password"
              />
              <Field
                icon={Lock}
                type="password"
                placeholder="confirma la contraseña"
                value={form.confirm}
                onChange={(v) => setForm({ ...form, confirm: v })}
                testid="register-confirm"
                required
                autoComplete="new-password"
              />

              <button
                type="button"
                onClick={() => setShowOptional((s) => !s)}
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-mono tracking-[0.3em] text-zinc-300 hover:text-rune-red border border-rune-red/20 hover:border-rune-red/60 rounded-sm transition-all"
                data-testid="toggle-optional"
              >
                <span>
                  {showOptional ? "OCULTAR" : "AÑADIR"} DATOS OPCIONALES{" "}
                  <span className="text-zinc-500">· TELÉFONO · UBICACIÓN</span>
                </span>
                {showOptional ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>

              {showOptional && (
                <div className="space-y-3 p-3 border border-rune-red/15 rounded-sm bg-black/30">
                  <Field
                    icon={User}
                    placeholder="nombre completo"
                    value={form.full_name}
                    onChange={(v) => setForm({ ...form, full_name: v })}
                    testid="register-name"
                    autoComplete="name"
                  />
                  <Field
                    icon={Phone}
                    type="tel"
                    placeholder="+52 55 1234 5678"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    testid="register-phone"
                    autoComplete="tel"
                  />
                  <Field
                    icon={MapPin}
                    placeholder="dirección (opcional)"
                    value={form.address}
                    onChange={(v) => setForm({ ...form, address: v })}
                    testid="register-address"
                    autoComplete="street-address"
                  />
                  <button
                    type="button"
                    onClick={() => geo.request()}
                    className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-[10px] font-mono tracking-[0.3em] rounded-sm transition-all ${
                      geo.status === "granted"
                        ? "border border-data-cyan/40 bg-data-cyan/10 text-data-cyan"
                        : geo.status === "denied" || geo.status === "error"
                        ? "border border-rune-red/40 bg-rune-red/10 text-rune-red"
                        : "border border-rune-red/20 text-zinc-300 hover:border-rune-red/60 hover:text-rune-red"
                    }`}
                    data-testid="register-gps-btn"
                  >
                    <MapPin size={12} />
                    {geo.status === "asking" && "PIDIENDO GPS..."}
                    {geo.status === "granted" &&
                      `📍 UBICACIÓN GUARDADA · ±${Math.round(
                        geo.coords?.accuracy || 0
                      )}m`}
                    {geo.status === "denied" && "PERMISO DENEGADO · OK"}
                    {geo.status === "error" && "GPS NO DISPONIBLE"}
                    {geo.status === "unsupported" && "GPS NO SOPORTADO"}
                    {geo.status === "idle" && "USAR MI UBICACIÓN GPS"}
                  </button>
                  <Field
                    icon={Gift}
                    placeholder="código de referido (opcional)"
                    value={form.referral_code}
                    onChange={(v) =>
                      setForm({ ...form, referral_code: v.toUpperCase() })
                    }
                    testid="register-referral"
                    className="tracking-[0.3em] font-bold uppercase"
                  />
                </div>
              )}

              {error && (
                <div
                  className="text-xs font-mono text-rune-red border border-rune-red/40 px-3 py-2 rounded-sm"
                  style={{ background: "rgba(40, 0, 10, 0.4)" }}
                  data-testid="register-error"
                >
                  ⚠ {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || deviceTaken}
                data-testid="register-submit"
                className="w-full relative px-7 py-3 clip-rune font-runic tracking-[0.3em] text-sm text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                style={{
                  background:
                    "linear-gradient(135deg, #ff1a3a 0%, #b8001f 50%, #5a0008 100%)",
                  boxShadow:
                    "0 0 24px rgba(255,26,58,0.4), inset 0 1px 0 rgba(255,255,255,0.20), inset 0 -8px 18px rgba(90,0,8,0.5)",
                }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> CREANDO…
                    </>
                  ) : (
                    <>
                      <Eye size={14} strokeWidth={2.5} /> ABRIR EL OJO
                    </>
                  )}
                </span>
              </button>

              <div className="text-[10px] font-mono tracking-[0.3em] text-zinc-500 mt-3 px-1">
                💡 ¿Ya usas el bot en Telegram? Podrás vincular tu cuenta después de entrar, desde tu panel.
              </div>

              <div className="text-center text-[10px] font-mono tracking-widest text-zinc-500 mt-4 pt-4 border-t border-rune-red/15">
                ¿YA TIENES CUENTA?{" "}
                <Link
                  to="/login"
                  className="text-rune-red hover:text-white hover:underline transition-colors"
                  data-testid="link-login"
                >
                  INICIAR SESIÓN
                </Link>
              </div>
            </form>
          </div>

          <div
            className="absolute bottom-0 left-0 right-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, #00e0ff 30%, #ff1a3a 70%, transparent)",
              boxShadow: "0 0 10px rgba(0,224,255,0.6)",
            }}
          />
        </div>

        <div className="text-center mt-4 text-[9px] font-mono tracking-[0.3em] text-zinc-600">
          ⚡ 1 CUENTA POR DISPOSITIVO · 3 MÁX POR RED · ZERO TOLERANCIA
        </div>
      </div>
    </div>
  );
}

function Field({
  icon: Icon,
  placeholder,
  value,
  onChange,
  type = "text",
  testid,
  className = "",
  required = false,
  autoComplete,
}) {
  return (
    <div className="relative">
      <Icon
        size={15}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-rune-red/70 pointer-events-none"
      />
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        autoComplete={autoComplete}
        className={`w-full bg-black/50 border border-rune-red/25 pl-10 pr-3 py-2.5 text-sm font-mono text-white placeholder:text-zinc-600 focus:outline-none focus:border-rune-red focus:shadow-[0_0_18px_rgba(255,26,58,0.35)] rounded-sm transition-all ${className}`}
      />
    </div>
  );
}
